/*
 * Alpine.data() component registrations for the ScanYerVan site.
 *
 * The site runs the Alpine.js CSP build (@alpinejs/csp) so the Content-Security-Policy
 * needs no 'unsafe-eval'. The CSP build evaluates directives with its own parser, not
 * eval()/new Function(): x-data must name a component registered here (Alpine.data), and
 * server state reaches it through data-* attributes read in init().
 *
 * What a directive expression may use (checked against the 3.15.8 parser, 2026-09-30):
 * property access, method calls, literals, ternaries (a ? b : c), && / ||, comparisons (===)
 * and arithmetic. NOT available: ?? (nullish coalescing) and multi-statement handlers. Anything
 * beyond a simple expression belongs in a getter or method below. Estate rule: FE-021.
 *
 * Loaded (defer) immediately before the Alpine script in every layout, and registers via the
 * alpine:init event — same convention as shade-slider.js / qr-progress.js.
 */
(function () {
    // ── "Are you sure?" on a form: data-confirm="question" ──────────────────────────────
    // An inline onsubmit="return confirm(...)" is an inline script, which the nonce-only CSP blocks - so admin forms
    // that create Stripe prices or end discounts submitted without asking (2026-09-30). The question now lives in the
    // form's data-confirm attribute and this one listener (capture phase, ahead of htmx/Alpine) asks it.
    document.addEventListener('submit', function (e) {
        var form = e.target;
        var question = form && form.getAttribute ? form.getAttribute('data-confirm') : null;
        if (question && !window.confirm(question)) {
            e.preventDefault();
            e.stopImmediatePropagation();
        }
    }, true);

    // ── Dialog focus (WCAG 2.4.3 / 2.1.2) ────────────────────────────────────────────
    // Shared by the dashboard "More" drawer and the feedback modal: while a dialog is open, focus starts inside it,
    // Tab wraps around it, and focus that escapes (including out of the bottom of the survey iframe, whose own Tab
    // presses never reach this page) is pulled back. Closing hands focus back to whatever opened it.
    const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
                      'textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';

    function focusablesIn(el) {
        return Array.from(el.querySelectorAll(FOCUSABLE)).filter(n => n.offsetParent !== null);
    }

    function focusFirstIn(el) {
        const first = focusablesIn(el)[0];
        (first || el).focus();
    }

    // Tab from the last element goes to the first, Shift+Tab from the first to the last.
    function wrapTab(el, e) {
        const items = focusablesIn(el);
        if (items.length === 0) { e.preventDefault(); el.focus(); return; }
        const first = items[0], last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    // Focus that lands outside the open dialog is brought back to its first control.
    function keepFocusIn(el, e) {
        if (!el.contains(e.target)) focusFirstIn(el);
    }

    function register() {

        // ── Pricing page: sticker material/colour preview ────────────────────────────
        // Pages/Shared/Partials/_StickerPrintPrices.cshtml — the sticker price panel on /plans.
        Alpine.data('stickerPricingPreview', () => ({
            material: 'standard',
            colour: 'bw',
            cta: 'SCAN ME',
            colours: {
                standard: {
                    bw:     { label: 'B&W',    bg: '#111111', qr: '#111111', ctaBg: '#111111', ctaText: '#FFFFFF', border: false },
                    colour: { label: 'Colour', bg: '#8B5CF6', qr: '#111111', ctaBg: '#8B5CF6', ctaText: '#FFFFFF', border: false }
                },
                // Fluorescent swatches are injected from the DB (reference.NeonColourOption) via
                // data-highviz on the root element, so this demo can never drift from the product. See init().
                highviz: {}
            },

            // Build the highviz swatches from the server-provided catalogue (single source of truth).
            init() {
                let hv = {};
                try {
                    const list = JSON.parse(this.$el.dataset.highviz || '[]');
                    list.forEach(c => {
                        hv[c.key] = { label: c.label, bg: c.hex, qr: '#111111', ctaBg: c.hex, ctaText: c.ctaText, border: false };
                    });
                } catch (e) { /* leave empty; the Standard swatches still render */ }
                this.colours.highviz = hv;
            },

            // Currently selected swatch descriptor.
            get v() { return this.colours[this.material][this.colour]; },
            get isStandard() { return this.material === 'standard'; },
            get isHighviz() { return this.material !== 'standard'; },

            setMaterial(m) {
                this.material = m;
                if (m === 'standard') { this.colour = 'bw'; return; }
                this.colour = this.colours.highviz.yellow ? 'yellow' : (Object.keys(this.colours.highviz)[0] || 'yellow');
            },
            selectColour(c) { this.colour = c; },

            // ── computed class / style helpers (no inline expressions in CSP build) ──
            materialBtnClass(m) {
                return this.material === m
                    ? 'bg-accent text-white shadow'
                    : 'text-white/70 hover:text-white';
            },
            colourBtnClass(c) {
                return this.colour === c
                    ? 'ring-2 ring-white ring-offset-1 ring-offset-[var(--sign-blue)] opacity-100'
                    : 'opacity-60 hover:opacity-90';
            },
            get vBorderClass() { return this.v.border ? 'ring-2 ring-black' : ''; },
            get vStyle() {
                return { backgroundColor: this.v.bg, '--cta-bg': this.v.ctaBg, '--cta-text': this.v.ctaText };
            },
            get qrStyle() { return { color: this.v.qr }; },
            get ctaStyle() { return { color: this.v.ctaText }; }
        }));

        // ── OTP entry countdown ──────────────────────────────────────────────────────
        // Pre-registration OTP panel (_PreRegOtpEntry).
        // Server values are read from data-* on the root element (no inline server injection in
        // the CSP build). data-resend (a resend cooldown) is optional.
        Alpine.data('otpCountdown', () => ({
            remaining: 0,
            resendIn: 0,
            _timer: null,

            init() {
                this.remaining = parseInt(this.$el.dataset.ttl || '0', 10);
                this.resendIn  = parseInt(this.$el.dataset.resend || '0', 10);
                this._timer = setInterval(() => {
                    if (this.remaining > 0) this.remaining--;
                    if (this.resendIn > 0) this.resendIn--;
                    if (this.remaining <= 0 && this.resendIn <= 0) {
                        clearInterval(this._timer);
                        this._timer = null;
                    }
                }, 1000);
            },
            destroy() {
                if (this._timer) { clearInterval(this._timer); this._timer = null; }
            },

            get expired() { return this.remaining <= 0; },
            get countdown() {
                if (this.remaining <= 0) return 'Expired';
                const m = Math.floor(this.remaining / 60), s = this.remaining % 60;
                return m + ':' + (s < 10 ? '0' + s : s);
            },
            get canResend() { return this.resendIn <= 0; },
            get showResendCountdown() { return this.resendIn > 0; },
            get resendCountdown() {
                const s = this.resendIn % 60;
                return '0:' + (s < 10 ? '0' + s : s);
            },

            // Digit-only filter; auto-submit once 6 digits are entered.
            onOtpInput(e) {
                const el = e.target;
                el.value = el.value.replace(/[^0-9]/g, '').slice(0, 6);
                if (el.value.length === 6 && !this.expired) this.$refs.otpForm.requestSubmit();
            },
            resend() { this.$refs.resendForm.requestSubmit(); }
        }));

        // ── Plans page: monthly / annual billing toggle ──────────────────────────────
        // Pages/Plans/Index.cshtml — flips the price cards between monthly and annual.
        Alpine.data('planBillingToggle', () => ({
            annual: false,
            setMonthly() { this.annual = false; },
            setAnnual() { this.annual = true; },
            get showMonthly() { return !this.annual; },
            get monthlyBtnClass() {
                return !this.annual ? 'bg-accent text-white shadow' : 'text-primary/70 hover:text-primary';
            },
            get annualBtnClass() {
                return this.annual ? 'bg-accent text-white shadow' : 'text-primary/70 hover:text-primary';
            }
        }));

        // ── Cancel subscription: reason selection ────────────────────────────────────
        // Dashboard/CancelSubscription.cshtml — reveals the free-text box when "Other" is ticked.
        // otherReasonId is read from data-other-reason-id (server value) in init().
        Alpine.data('cancelReasons', () => ({
            selected: [],
            otherReasonId: 0,
            otherFreeText: '',
            init() { this.otherReasonId = parseInt(this.$el.dataset.otherReasonId || '0', 10); },
            get hasOther() { return this.otherReasonId > 0 && this.selected.includes(this.otherReasonId); },
            toggleReason(id, checked) {
                if (checked) this.selected.push(id);
                else this.selected = this.selected.filter(x => x !== id);
            }
        }));

        // ── QR config / checkout section ─────────────────────────────────────────────
        // _QrConfigSection.cshtml — the sticker-design + proceed-to-payment step of signup.
        // All server-rendered initial state is read from data-* on the root (booleans as
        // "true"/"false"); the original inline getters, init() wiring and $watch()es are ported
        // verbatim below. Two multi-statement handlers (reset-to-auto, submit) became methods.
        Alpine.data('qrConfigSection', () => ({
            hvSelected: false,
            brandedSelected: false,
            useAutoHighViz: true,
            hasMixed: false,
            recommendedMaterial: '',
            previewCount: 0,
            multiMaterialConfirmed: false,
            brandColourHex: '#1966ca',
            logoFileName: '',
            logoUrl: '',
            logoError: '',   // why the last upload was turned down (too small, would vanish, wrong file)
            logoNote: '',    // something to know about the logo that was saved (kept its background, may look soft)
            mixedSaving: 0,
            qrTested: false,
            ctaTouched: false,
            ctaHasValue: false,
            positionCount: 0,
            _brandColourTimer: null,

            get canProceed() {
                return this.positionCount > 0 &&
                       this.previewCount > 0 &&
                       this.qrTested &&
                       this.ctaHasValue &&
                       !(this.hvSelected && this.hasMixed && this.useAutoHighViz && !this.multiMaterialConfirmed);
            },

            init() {
                const d = this.$el.dataset;
                this.hvSelected             = d.hvSelected === 'true';
                this.brandedSelected        = d.brandedSelected === 'true';
                this.useAutoHighViz         = d.useAutoHighViz !== 'false';
                this.hasMixed               = d.hasMixed === 'true';
                this.recommendedMaterial    = d.recommendedMaterial || '';
                this.previewCount           = parseInt(d.previewCount || '0', 10);
                this.multiMaterialConfirmed = d.multiMaterialConfirmed === 'true';
                this.brandColourHex         = d.brandColourHex || '#1966ca';
                this.logoFileName           = d.logoFileName || '';
                this.logoUrl                = d.logoUrl || '';
                this.qrTested               = d.qrTested === 'true';
                this.ctaTouched             = d.ctaTouched === 'true';
                this.ctaHasValue            = d.ctaHasValue === 'true';
                this.positionCount          = parseInt(d.positionCount || '0', 10);

                this.$el.addEventListener('change', e => {
                    if (e.target.name === 'QrConfig.Style') {
                        this.hvSelected = (e.target.value === 'HighViz');
                        this.brandedSelected = (e.target.value === 'Branded');
                        this.useAutoHighViz = true;
                        Array.from(this.$el.querySelectorAll('input[type=radio]'))
                            .filter(r => r.name.indexOf('FluorescentColour') !== -1)
                            .forEach(r => { r.checked = false; });
                    }
                    if (e.target.name === 'QrConfig.FluorescentColour')
                        this.useAutoHighViz = false;
                    if (e.target.name === 'QrConfig.Positions')
                        this.positionCount = this.$el.querySelectorAll("input[name='QrConfig.Positions']:checked").length;
                });
                document.addEventListener('regen-qr', () => {
                    // Only regen when the QR config accordion is the active step.
                    if (!document.getElementById('acc-codes')?.hasAttribute('open')) return;
                    const form = document.getElementById('qr-config-form');
                    if (!form || !window.htmx) return;
                    const flag = form.querySelector('#fleetChangeOnly');
                    if (flag) flag.value = 'true';
                    window.htmx.trigger(form, 'regen');
                });
                document.getElementById('qr-config-form')
                    ?.addEventListener('htmx:afterRequest', () => {
                        const flag = document.getElementById('fleetChangeOnly');
                        if (flag) flag.value = 'false';
                    });
                document.addEventListener('fluor-mixed-updated', e => { this.hasMixed = e.detail.hasMixed; });
                document.addEventListener('fluor-rec-updated', e => {
                    if (e.detail.material) this.recommendedMaterial = e.detail.material;
                });
                document.addEventListener('preview-count-updated', e => { this.previewCount = e.detail.count; });
                document.addEventListener('htmx:afterSettle', () => {
                    const qs = document.getElementById('quote-summary');
                    if (qs && qs.dataset.singleSaving) {
                        const s = parseFloat(qs.dataset.singleSaving);
                        if (!isNaN(s) && s > 0) this.mixedSaving = s;
                    }
                });
                document.addEventListener('logo-uploaded', e => {
                    if (e.detail.fileName) this.logoFileName = e.detail.fileName;
                    if (e.detail.logoUrl)  this.logoUrl      = e.detail.logoUrl;
                    this.logoError = '';
                    this.logoNote  = e.detail.note || '';
                });
                // Sent by the server since the upload rules were written, and never listened for: a refused logo
                // looked like nothing had happened (found 2026-10-04). htmx passes a plain string as { value }.
                document.addEventListener('logo-rejected', e => {
                    const d = e.detail;
                    this.logoError = (typeof d === 'string' ? d : d && d.value) || 'That image could not be accepted.';
                    this.logoNote  = '';
                });
                // Re-generate previews when the brand colour changes (debounced) so the Branded
                // border ring baked into the QR PNG stays in sync with the colour picker.
                this.$watch('brandColourHex', () => {
                    clearTimeout(this._brandColourTimer);
                    this._brandColourTimer = setTimeout(() => {
                        const form = document.getElementById('qr-config-form');
                        if (form && window.htmx) window.htmx.trigger(form, 'regen');
                    }, 600);
                });
                this.$watch('hasMixed',       () => { this.multiMaterialConfirmed = false; });
                this.$watch('useAutoHighViz', () => { this.multiMaterialConfirmed = false; });
                // If the tested checkbox is ticked without a CTA value, surface the error and untick.
                this.$watch('qrTested', val => {
                    if (val && !this.ctaHasValue) {
                        this.ctaTouched = true;
                        this.$nextTick(() => { this.qrTested = false; });
                    }
                });
            },

            // "Reset to auto" — clear manual fluorescent choices and regenerate.
            resetToAuto() {
                this.useAutoHighViz = true;
                Array.from(this.$el.querySelectorAll("input[name='QrConfig.FluorescentColour']"))
                    .forEach(r => { r.checked = false; });
                this.$nextTick(() => {
                    const form = document.getElementById('qr-config-form');
                    if (form && window.htmx) window.htmx.trigger(form, 'regen');
                });
            },

            // CTA preset chip clicked — fill the sticker-message field. Lives here (not inline) because
            // the Alpine CSP build can't evaluate multi-statement inline handlers (or `new Event(...)`).
            // Dispatching a real `input` event lets qr-config.js update the char counter and fires the
            // form's `input from:#ctaText` htmx trigger, so the live preview regenerates with the new text.
            setCta(text) {
                // $refs first, then the id - doing nothing quietly is never the right answer for a click
                // someone made on purpose.
                const el = this.$refs?.cta || document.getElementById('ctaText');
                if (!el) {
                    console.warn('[qrConfigSection] setCta: no #ctaText to write "%s" into.', text);
                    return;
                }

                el.value = text;
                this.ctaHasValue = !!text.trim();

                // Only after the value is actually in the field. Marking it touched on a click that set
                // nothing is what puts the error on screen with an empty box.
                if (this.ctaHasValue) this.ctaTouched = true;

                // A real `input` event: qr-config.js updates the character counter from it, and the form's
                // `input from:#ctaText` htmx trigger regenerates the live preview with the new text.
                el.dispatchEvent(new Event('input', { bubbles: true }));
            },

            // Pressing a preset chip must not take focus from the sticker-message field. The field is
            // autofocused on desktop, so a press blurred it, @blur marked it touched, and "Required — ..."
            // appeared between the field and the chips - moving them ~34px down before the button came up,
            // so the release landed off the chip and the click never reached setCta. First click "did
            // nothing but show the error", second click worked (reproduced in Chromium 2026-10-21; phones
            // ignore autofocus, so they never saw it). Keeping focus means no blur, no shift, and the field
            // stays ready to edit. Keyboard use is unaffected: Tab still moves focus to a chip.
            keepCtaFocus(e) {
                e.preventDefault();
            },

            // Proceed-to-payment submit guard — block + focus the CTA field when not ready.
            onProceedSubmit(e) {
                if (this.canProceed) return;
                e.preventDefault();
                this.ctaTouched = true;
                if (this.positionCount === 0) {
                    // Nothing picked at all. The inline hint by the picker is already showing; take them
                    // to it rather than leaving a dead button.
                    this.$nextTick(() => {
                        const el = document.getElementById('sticker-positions');
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    });
                } else if (!this.ctaHasValue) {
                    this.$nextTick(() => {
                        const el = document.getElementById('ctaText');
                        if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); }
                    });
                }
            },

            // Brand-colour hex text field: accept only a full #rrggbb value (_BrandColourAddonExpansion).
            onBrandHexChange(e) {
                if (/^#[0-9a-fA-F]{6}$/.test(e.target.value)) this.brandColourHex = e.target.value;
            },

            // Block the proceed htmx request when the pay button is disabled (was hx-on:htmx:before-request).
            onBeforeProceed(e) {
                if (document.getElementById('proceed-payment-btn')?.disabled) e.preventDefault();
            }
        }));

        // ── OTP digit input that auto-clicks a verify button ─────────────────────────
        // _CodeEntry.cshtml — filters to 6 digits, then clicks #otp-verify-btn.
        Alpine.data('otpVerifyInput', () => ({
            submitted: false,
            onInput(e) {
                const el = e.target;
                el.value = el.value.replace(/[^0-9]/g, '').slice(0, 6);
                if (el.value.length === 6) {
                    const btn = document.getElementById('otp-verify-btn');
                    if (btn && !btn.disabled) btn.click();
                }
            },
            // A code is single-use: the automatic submit and a press of the button (or a double-click) must not post it
            // twice - the second post would fail and could replace the good answer with an error.
            onSubmit(e) {
                if (this.submitted) { e.preventDefault(); return; }
                this.submitted = true;
                const btn = document.getElementById('otp-verify-btn');
                if (btn) btn.disabled = true;
            }
        }));

        // ── Give-feedback modal (dashboard) ──────────────────────────────────────────
        // Pages/Shared/Partials/_FeedbackModal.cshtml — an on-brand iframe embed of the GENERAL survey,
        // HTMX-swapped into #feedback-modal. Formbricks link surveys emit no completion signal to the
        // embedding page, so a hidden poller (#fb-survey-poll) hits ?handler=FeedbackStatus&tid=… every 2s;
        // once the webhook has recorded the response the handler returns HX-Trigger: feedback-complete, which
        // bubbles to this window listener (x-on:feedback-complete.window="finish()"). finish() swaps the
        // iframe for the thank-you (x-show) and closes the modal shortly after. close() / finish() both remove
        // the poller so it stops ticking (the modal stays in the DOM, hidden, until the next open replaces it).
        Alpine.data('feedbackModal', () => ({
            open: true,
            done: false,
            _closeTimer: null,
            _opener: null,

            // The survey is shown until completion is detected (no inline "!done" under the CSP build).
            get showSurvey() { return !this.done; },

            // Swapped in already open: remember what opened it (the "Give feedback" button) and move focus inside.
            init() {
                this._opener = document.activeElement;
                this.$nextTick(() => focusFirstIn(this.$refs.dialog));
            },

            // Backdrop / X / Escape — dismiss and stop polling.
            close() {
                if (!this.open) return;
                this.open = false;
                this._stopPoll();
                this._returnFocus();
            },

            // Completion detected via the poller's HX-Trigger. Show the thank-you, stop polling, then close.
            finish() {
                if (this.done) return;
                this.done = true;
                this._stopPoll();
                this._closeTimer = setTimeout(() => { this.open = false; this._returnFocus(); }, 1500);
            },

            onKeydown(e) {
                if (this.open && e.key === 'Tab') wrapTab(this.$refs.dialog, e);
            },

            onFocusin(e) {
                if (this.open) keepFocusIn(this.$refs.dialog, e);
            },

            _returnFocus() {
                if (this._opener && document.contains(this._opener)) this._opener.focus();
            },

            destroy() {
                if (this._closeTimer) { clearTimeout(this._closeTimer); this._closeTimer = null; }
            },

            _stopPoll() {
                const poll = document.getElementById('fb-survey-poll');
                if (poll) poll.remove();
            }
        }));

        // ── Copy a link (dashboard Refer page) ───────────────────────────────────────
        // Pages/Dashboard/ReferAFriend.cshtml — copies the share link; where the clipboard is refused, selects the text
        // so the referrer can copy it by hand.
        Alpine.data('copyLink', () => ({
            copied: false,
            _timer: null,

            get label() { return this.copied ? 'Copied' : 'Copy link'; },

            copy() {
                const field = this.$refs.link;
                navigator.clipboard.writeText(field.value).then(() => {
                    this.copied = true;
                    clearTimeout(this._timer);
                    this._timer = setTimeout(() => { this.copied = false; }, 2000);
                }).catch(() => { field.focus(); field.select(); });
            },

            destroy() { clearTimeout(this._timer); }
        }));

        // ── Dashboard "More" drawer (mobile) ─────────────────────────────────────────
        // Pages/Shared/_DashboardLayout.cshtml — the sheet above the tab bar holding the pages the bar has no room
        // for. A dialog: the button says whether it is open, focus moves into the sheet and back to the button,
        // Escape and the backdrop close it, and Tab stays inside while it is open (P2-020).
        Alpine.data('moreDrawer', () => ({
            open: false,

            toggle() {
                if (this.open) this.close();
                else this.show();
            },

            show() {
                this.open = true;
                this.$nextTick(() => focusFirstIn(this.$refs.sheet));
            },

            close() {
                if (!this.open) return;
                this.open = false;
                this.$refs.button.focus();
            },

            // A link inside was followed - the page is changing, so no focus hand-back.
            leave() {
                this.open = false;
            },

            onKeydown(e) {
                if (!this.open) return;
                if (e.key === 'Escape') this.close();
                else if (e.key === 'Tab') wrapTab(this.$refs.sheet, e);
            },

            onFocusin(e) {
                if (this.open) keepFocusIn(this.$refs.sheet, e);
            }
        }));

        // ── Delivery address section (payment panel) ─────────────────────────────────
        // _PaymentPanel.cshtml — postcode-lookup delivery address. Plain state (showFields/
        // addressPicked/pickedLabel) is also driven externally by stripe-payment.js via
        // Alpine.$data(); only the "Change" reset needed a method (multi-statement + $dispatch).
        Alpine.data('deliveryAddress', () => ({
            showFields: false,
            addressPicked: false,
            pickedLabel: '',
            init() { this.showFields = this.$el.dataset.showFields === 'true'; },
            resetAddress() {
                this.addressPicked = false;
                this.showFields = false;
                this.$dispatch('delivery-address-reset');
            }
        }));

    }

    // ── CSP-safe replacement for the DVLA reg-input hx-trigger filters (_VehicleRegInput) ──
    // The eval-based hx-trigger="keyup[len>=3]…, blur[…]" cannot run under htmx allowEval:false,
    // so the markup trigger is now plain "keyup delay:1000ms, blur" and this gate cancels the
    // requests those filters would have suppressed: registration shorter than 3 chars, and a blur
    // re-lookup once the vehicle is already added (add button enabled). htmx:configRequest is
    // cancelable and carries the triggering event so keyup vs blur can be told apart.
    document.addEventListener('htmx:configRequest', function (evt) {
        const el = evt.target;
        if (!el || el.id !== 'VehicleRegistration_Registration') return;
        const len = (el.value || '').replace(/\s/g, '').length;
        if (len < 3) { evt.preventDefault(); return; }
        const trig = evt.detail && evt.detail.triggeringEvent;
        if (trig && trig.type === 'blur') {
            const btn = document.getElementById('add-vehicle-btn');
            if (btn && btn.disabled === false) evt.preventDefault();
        }
    });

    if (window.Alpine) register();
    else document.addEventListener('alpine:init', register);
})();
